/**
 * Slack-parity message extras (docs/design/slack-gap.md): message search with filters, pinned messages, mark unread.
 * Message links need no route: they are app URLs with `?m=<messageId>` (core `Shell.messageUrl`).
 */
import { z } from "zod";
import { Id, Message } from "./entities.ts";

/**
 * GET /v1/w/:ws/search?q=&limit= — messages matching a Slack-style query (core `logic/searchQuery`): free text plus
 * `from:@handle|me`, `in:#channel|@handle`, `with:@handle` (DMs / group DMs with them, threads they're in),
 * `has:link|file|pin|reaction`, `before:` / `after:` / `on:` (YYYY-MM-DD), `during:` (a day, `2026-10`, `october`,
 * `2026`) in the searcher's time zone, `is:thread`. Only conversations the caller can read; newest first.
 */
export const SearchMessagesQuery = z.object({
  q: z.string().max(500),
  limit: z.coerce.number().int().min(1).max(100).default(40),
  /** The searcher's IANA time zone for the date filters (absent: their profile's, else UTC). */
  tz: z.string().max(64).optional(),
});
export type SearchMessagesQuery = z.infer<typeof SearchMessagesQuery>;

export const SearchMessagesResponse = z.object({
  messages: z.array(Message),
  /** Filter values the server couldn't resolve (`from:@nobody`, `in:#gone`, a bad date): shown as a hint. */
  unresolved: z.array(z.string()).optional(),
});
export type SearchMessagesResponse = z.infer<typeof SearchMessagesResponse>;

/** GET /v1/w/:ws/channels/:channelId/pins — the conversation's pinned messages, most recently pinned first. */
export const ListPinsResponse = z.object({ messages: z.array(Message) });
export type ListPinsResponse = z.infer<typeof ListPinsResponse>;

/**
 * POST /v1/w/:ws/channels/:channelId/unread — "Mark unread" from a message: `seq` is that message's seq, so it and
 * everything after it become unread (last_read_seq = seq - 1). Unlike markRead it may move backwards. The caller's
 * other clients get `read.updated` with the new lastReadSeq.
 */
export const MarkUnreadRequest = z.object({ seq: z.number().int().positive() });
export type MarkUnreadRequest = z.infer<typeof MarkUnreadRequest>;

/** Most conversations one share goes to (Slack's forward dialog takes several destinations). */
export const MAX_SHARE_DESTINATIONS = 10;

/**
 * POST /v1/w/:ws/messages/:messageId/share — share (forward) a message I can read into conversations I can post in,
 * with one optional comment (`text`, may be empty) posted with each copy. Destinations: `channelIds` (1–10), or the
 * older single `channelId` (still accepted for older clients; one of the two is required). With several destinations
 * the copies' client ids are `clientId:<index>`. → ShareMessageResponse: `messages` in destination order, `message` =
 * the first (what older clients read). Each new message has `shared` set (message.created). All-or-nothing: a
 * destination I can't post in refuses the whole share.
 */
export const ShareMessageRequest = z.object({
  channelId: Id.optional(),
  channelIds: z.array(Id).min(1).max(MAX_SHARE_DESTINATIONS).optional(),
  text: z.string().max(40_000).default(""),
  clientId: z.string().max(64).optional(),
});
export type ShareMessageRequest = z.infer<typeof ShareMessageRequest>;

export const ShareMessageResponse = z.object({ message: Message, messages: z.array(Message) });
export type ShareMessageResponse = z.infer<typeof ShareMessageResponse>;

/**
 * POST /v1/w/:ws/messages/:messageId/summary — "Summarize" / "Refresh summary" on a thread root I can read
 * (docs/features/thread-summaries.md). The summary is written in the background (a Cloudflare Workflow) and arrives
 * as `message.updated` on the root with `summary` set. `started`: a run began; `running`: one is already running for
 * this thread; `unchanged`: nothing changed since the current summary. 403 `ai_disabled` when the workspace turned
 * summaries off, 503 `ai_unavailable` when the server has no model key, 400 when the message is not a thread root.
 */
/** POST /v1/w/:ws/messages/:messageId/unfurls/hide — the author removes one link's preview (it doesn't come back on edit). */
export const HideUnfurlRequest = z.object({ url: z.string().min(1).max(2048) });
export type HideUnfurlRequest = z.infer<typeof HideUnfurlRequest>;

export const SummarizeThreadResponse =z.object({ status: z.enum(["started", "running", "unchanged"]) });
export type SummarizeThreadResponse = z.infer<typeof SummarizeThreadResponse>;
